import axios, { type AxiosResponse } from 'axios'
import { decodeUrvOfflineCheckinQr, type UrvOfflineCheckinPayload } from '@/utils/urvOfflineQr'

export interface UrvDepartment {
    id: string
    name: string
}

export interface UrvWorker {
    user_id: number
    name: string
    shift_start: string
    status: 'working' | 'late' | 'error' | 'absent' | 'shift_today'
    avatar: string
}

export interface UrvEmployees {
    on_shift: UrvWorker[]
    waiting_for: UrvWorker[]
}

interface UrvEnvelope<T> {
    status?: string
    body?: T
    msg?: string
}

export class UrvApiError extends Error {
    readonly isSessionInvalid: boolean

    constructor(message: string, isSessionInvalid = false) {
        super(message)
        this.name = 'UrvApiError'
        this.isSessionInvalid = isSessionInvalid
    }
}

const urvHttp = axios.create({
    baseURL: import.meta.env.VITE_API_BASE,
    withCredentials: true,
})

function getEnvelopeBody<T>(response: AxiosResponse<UrvEnvelope<T>>): T {
    const { data } = response

    if (data.status === 'error') {
        const message = data.msg || 'Не удалось выполнить запрос'
        throw new UrvApiError(message)
    }

    if (!data.body) {
        throw new UrvApiError('Сервер вернул неполный ответ')
    }

    return data.body
}

function createUrvApiError(error: unknown, fallbackMessage: string): UrvApiError {
    if (error instanceof UrvApiError) {
        return error
    }

    if (axios.isAxiosError(error)) {
        const status = error.response?.status
        const responseData = error.response?.data as UrvEnvelope<unknown> | undefined
        const message = responseData?.msg || fallbackMessage
        const isSessionInvalid = status === 401 || status === 403

        return new UrvApiError(message, isSessionInvalid)
    }

    return new UrvApiError(fallbackMessage)
}

export async function authorizeUrvKey(key: string): Promise<UrvDepartment> {
    let responseBody: { department?: UrvDepartment }

    try {
        responseBody = getEnvelopeBody<{ department?: UrvDepartment }>(
            await urvHttp.post('/api/auth-keys/auth/', { key }, { withCredentials: true }),
        )
    } catch (error) {
        throw createUrvApiError(error, 'Не удалось авторизовать ключ')
    }

    const department = responseBody.department
    if (!department || typeof department.id !== 'string' || typeof department.name !== 'string') {
        throw new UrvApiError('Сервер вернул неполный ответ')
    }

    return department
}

export async function getUrvQrSvg(departmentId: string): Promise<string> {
    let responseBody: { svg?: string }

    try {
        responseBody = getEnvelopeBody<{ svg?: string }>(
            await urvHttp.get(`/api/departments/${departmentId}/get-qr-svg/`, { withCredentials: true }),
        )
    } catch (error) {
        throw createUrvApiError(error, 'Не удалось получить QR-код')
    }

    if (typeof responseBody.svg !== 'string') {
        throw new UrvApiError('Сервер вернул неполный ответ')
    }

    return responseBody.svg
}

export async function getUrvEmployees(departmentId: string): Promise<UrvEmployees> {
    let responseBody: { data?: UrvEmployees }

    try {
        responseBody = getEnvelopeBody<{ data?: UrvEmployees }>(
            await urvHttp.post('/api/checkincheckout/users-in-department-info/', { id: departmentId }, { withCredentials: true }),
        )
    } catch (error) {
        throw createUrvApiError(error, 'Не удалось получить список сотрудников')
    }

    const data = responseBody.data
    if (!data || !Array.isArray(data.on_shift) || !Array.isArray(data.waiting_for)) {
        throw new UrvApiError('Сервер вернул неполный ответ')
    }

    return data
}

export type { UrvOfflineCheckinPayload }

const offlineCheckinTimeout = 15_000
const offlineCheckinUnconfirmedMessage = 'Результат отметки не подтверждён: сервер не ответил. Проверьте список сотрудников.'

export const urvInvalidQrMessage = 'QR-код не распознан'

function isFilledString(value: unknown): value is string {
    return typeof value === 'string' && value.length > 0
}

// QR содержит компактный транспорт WQ1; старый JSON не поддерживается (RD-178).
export function parseUrvOfflineCheckinQr(qrText: string): UrvOfflineCheckinPayload | null {
    return decodeUrvOfflineCheckinQr(qrText)
}

export async function makeUrvOfflineCheckin(payload: UrvOfflineCheckinPayload): Promise<string> {
    let response: AxiosResponse<unknown>

    try {
        response = await urvHttp.post<unknown>('/api/checkincheckout/make-offline-checkin/', payload, {
            withCredentials: true,
            timeout: offlineCheckinTimeout,
        })
    } catch (error) {
        const status = axios.isAxiosError(error) ? error.response?.status : undefined

        if (status === 401 || status === 403) {
            throw new UrvApiError('URV-сессия недействительна. Введите новый ключ доступа.', true)
        }

        if (status === 400) {
            throw new UrvApiError(urvInvalidQrMessage)
        }

        throw new UrvApiError(offlineCheckinUnconfirmedMessage)
    }

    const { data } = response
    const envelope = data && typeof data === 'object' ? data as UrvEnvelope<unknown> : undefined

    if (response.status !== 200 || !isFilledString(envelope?.msg)) {
        throw new UrvApiError(offlineCheckinUnconfirmedMessage)
    }

    if (envelope.status === 'error') {
        throw new UrvApiError(envelope.msg)
    }

    if (envelope.status !== 'ok') {
        throw new UrvApiError(offlineCheckinUnconfirmedMessage)
    }

    return envelope.msg
}
